import { Api, HorizontalAlignment, ModelRow } from "@mcleod/core";
import { AutogenLayoutCustomFuelSurchargeTableMaster } from "./autogen/AutogenLayoutCustomFuelSurchargeTableMaster";
import { ClickEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem, EditRowDecorator, Label, Layout, MultiButton, TableRowDisplayEvent, Textbox, DropdownSelectionEvent, Panel, TableRow, Snackbar, DataHeader, Toast, TableRowMode, Button } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { CustomFuelSurchargeBuildTable } from "./CustomFuelSurchargeBuildTable";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";

const viewCustomFscTableURL = "lme/ar/fuelsurcharge/CustomFuelSurchargeTableMaster?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName:"custom_fsc_hdr",
    screenName:"CustomFuelSurchargeTableMaster",
    navigateToUrl:viewCustomFscTableURL
};
export class CustomFuelSurchargeTableMaster extends AutogenLayoutCustomFuelSurchargeTableMaster {
    private acceptButtonOnClick: () => {};
    
    override onLoad(): void | Promise<void> {
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
    }

    /** This is an event handler for the afterModeChange event of sourceCustomFscHdr. */
    sourceCustomFscHdrAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.UPDATE) {
            this.tableCustomFscDetails.clear();
        }
    }

    /** This is an event handler for the onClick event of buttonBuildTable. */
    async buttonBuildTableOnClick(event: ClickEvent) {
        const headerId = this.mainDataSource.activeRow?.get("id");
        if (headerId == null) {
            CommonDialogs.showMessage("Please enter customer table code before generating the table.", "Missing Custom Table Code");
            return;
        }
        let duplicateHeaderId : boolean = false;
        if (DataSourceMode.ADD === this.mainDataSource.mode) {
            await Api.post("lme/ar/fuelsurcharge/custom-fsc-hdr-dtl-check", {
                header_id: this.mainDataSource.activeRow.get("id"),
                duplicate_hdr_check: true
            }).then( async (response) => {
                duplicateHeaderId = response.data[0].duplicate_hdr_found;
                if (response.data[0].duplicate_hdr_found === true ) {
                    Snackbar.showWarningSnackbar("Custom Table Code " + this.mainDataSource.activeRow.get("id")
                    +" already exists", {persist: true});
                }
            }).catch((error) => {
                CommonDialogs.showError(error);
            });
        }
        if (duplicateHeaderId) {
            return;
        }
        let buildTableLayout: CustomFuelSurchargeBuildTable;
        let saveButton: MultiButton;
        const erd = new EditRowDecorator({
            title: "Build Custom Fuel Surcharge Table",
            layout: buildTableLayout = Layout.getLayout("lme/ar/fuelsurcharge/CustomFuelSurchargeBuildTable") as CustomFuelSurchargeBuildTable,
            onSave: async () => {
                Api.post("lme/ar/fuelsurcharge/custom-fsc-hdr-dtl-check", {
                    header_id: this.mainDataSource.activeRow.get("id"),
                    start_price: buildTableLayout.textboxStart.getDataValue(),
                    end_price: buildTableLayout.textboxEnd.getDataValue()
                }).then( async (response) => {
                    const dataExists = response?.data[0]?.rows_in_price_range;
                    if (dataExists === true) {
                        const message = "Custom fuel surcharge detail rows already exist for header " + this.mainDataSource.activeRow.get("id") + " between the price "
                        + "range of " + buildTableLayout.textboxStart.getDataValue() + " and " + buildTableLayout.textboxEnd.getDataValue()
                        + ". \nThese records will be deleted if you continue.\n\n"
                        + "Do you wish to continue?";
                        const messagePanel = new Panel({ rowBreak: true, wrap: true });
                        messagePanel.add(new Label({ text: message, align: HorizontalAlignment.CENTER}))
                        const deleteRows = await CommonDialogs.showYesNo(messagePanel, "Confirm Deletion");
                        if (deleteRows === true)
                            this.generateTable(buildTableLayout);
                    }
                    else {
                        this.generateTable(buildTableLayout);
                    }

                }).catch((error) => {
                    CommonDialogs.showError(error);
                });

            },
            width: '45%',
            onClose: () => buildTableLayout.slideOut(),
            saveButtonProps: {caption: "Generate"},
            doAfterSlideIn: (decorator: EditRowDecorator) => {
                saveButton = decorator.multiButton;
                saveButton.primaryButton.caption = "Generate";
            }
        });
    }

    /** This is an event handler for the onRowDisplay event of tableCustomFscDetails. */
    tableCustomFscDetailsOnRowDisplay(event: TableRowDisplayEvent) {
        const table = event.getTable();
        const rowCount = table.dataSource.rowCount;
        const tableRow = event.getTableRow();
        const modelRow = tableRow?.data as ModelRow;
        const endPriceLabel = tableRow.findComponentById("labelEndPrice") as Label;
        if (tableRow.index+1 === rowCount) {
            endPriceLabel.text = null;
        }
        const nextRowData: ModelRow = table.dataSource.data[tableRow.index+1];
        const endPrice = nextRowData ? (nextRowData?.get("start_price") as number) - 0.001 : 0.000;
        endPriceLabel.text = endPrice.toFixed(3);
        const methodTextbox = tableRow.findComponentById("textboxMethod") as Textbox;
        methodTextbox.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (event.hasSelection()) {
                this.setupDetailsTableRequiredFields(tableRow, event.selection.value);
            }
        })
        this.setupDetailsTableRequiredFields(tableRow, modelRow?.get("method"));
    }

    async generateTable(buildTableLayout: CustomFuelSurchargeBuildTable) {
        await Api.post("lme/ar/fuelsurcharge/custom-fsc-generate-table", {
            header_id: this.mainDataSource.activeRow.get("id"),
            start_price: buildTableLayout.textboxStart.getDataValue(),
            end_price: buildTableLayout.textboxEnd.getDataValue(),
            base_charge: buildTableLayout.textboxBaseCharge.getDataValue(),
            fuel_price_increment: buildTableLayout.textboxFuelPriceIncrement.getDataValue(),
            surcharge_price_increment: buildTableLayout.textboxSurchargeIncrememnt.getDataValue(),
            method: (buildTableLayout.textboxMethod.items as DropdownItem[]).find(item => item.displayValue === buildTableLayout.textboxMethod.getDataValue()).value,
            current_data: this.tableCustomFscDetails.dataSource.data
        }).then( async (response) => {
            if (response?.data?.[0]?.success === true) {
                    for (let i = this.tableCustomFscDetails.rows.length - 1; i >= 0; i--) {
                        const row = this.tableCustomFscDetails.rows[i];
                        row.deleteRow();
                    }
                    const mapFscDetails = response.data[0].new_detail_rows;
                    const modelRows: ModelRow[] = mapFscDetails.map(row => new ModelRow(this.sourceCustomFscDtl.url, true, row));

                    modelRows.forEach(row => { this.tableCustomFscDetails.addRow(row, {mode:TableRowMode.NONE}, {addToData:true, display:true, save:false} ) });
                Toast.showSuccessToast("Success!", "Custom fuel surcharge table generated successfully.");
            }
        }).catch((error) => {
            CommonDialogs.showError(error);
        });
    }

    setupDetailsTableRequiredFields(tableRow: TableRow, method: string) {
        switch (method) {
            case "D":
            case "F":
                tableRow.findComponentById("textboxAmount").enabled = true;
                tableRow.findComponentById("textboxAmount").required = true;
                tableRow.findComponentById("textboxPercentage").required = false;
                (tableRow.findComponentById("textboxPercentage") as Textbox).text = null;
                tableRow.findComponentById("textboxPercentage").enabled = false;
                break;
            case "P":
                tableRow.findComponentById("textboxAmount").enabled = false;
                (tableRow.findComponentById("textboxAmount") as Textbox).text = null;
                tableRow.findComponentById("textboxAmount").required = false;
                tableRow.findComponentById("textboxPercentage").enabled = true;
                tableRow.findComponentById("textboxPercentage").required = true;
                break;
        }
    }

    get acceptButton(): Button {
        return new Button({
            id: "acceptButton",
            caption: "Save",
            backgroundColor: "primary",
            color: "McLeodWhite",
            minWidth: 128,
            fontBold: true,
            borderWidth: 0,
            rowBreak: false,
            onClick: () => {
                this.acceptButtonOnClick();
            }
        });
    }

    set acceptButtonCallback(callback) {
        this.acceptButtonOnClick = callback;
    }
}
